import React, { useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { motion, type AnimationControls } from 'framer-motion';
import {
  PAYWALL_CONTENT_MAX_WIDTH_CLASS,
  PAYWALL_EXTENSION_BADGE_COLORS,
  PAYWALL_TOOL_FEATURES,
} from '@/features/tool/constants/paywall';
import {
  PaywallCheckoutFrame,
  PaywallPdfPage,
  PaywallPriceText,
  PaywallThumbPlaceholder,
} from './components';
import { ReadyCheckIcon, TrialRocketIcon, ZoomInIcon } from './icons';
import type { PaywallCheckoutProps, PaywallDocumentProps } from './types';
import { splitFileName } from './utils';

const Paywall1FeatureGrid = () => (
  <section>
    <h3 className="text-[20px] font-bold leading-6 text-[#292A33]">
      Everything unlocks the moment you start
    </h3>
    <div className="mt-4 grid grid-cols-3 gap-1 lg:gap-3">
      {PAYWALL_TOOL_FEATURES.map(({ title, description, color, Icon }) => (
        <article
          className="rounded-[10px] bg-white p-3 lg:min-h-[132px]"
          key={title}
        >
          <span
            className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#F6F7F8]"
            style={{ color }}
          >
            <Icon className="h-6 w-6" aria-hidden />
          </span>
          <h4 className="mt-2 text-[15px] font-semibold leading-5 text-[#292A33] lg:text-[17px] lg:leading-[22px]">
            {title}
          </h4>
          <p className="mt-0.5 text-[13px] leading-4 text-[#787887] lg:text-[15px] lg:leading-[18px]">
            {description}
          </p>
        </article>
      ))}
    </div>
  </section>
);

const Paywall1DocumentThumb = ({
  file,
  extension,
}: {
  file?: File | null;
  extension: string;
}) => {
  const badge = PAYWALL_EXTENSION_BADGE_COLORS[extension] ?? 'bg-gray-400';
  const isPdf = file?.type === 'application/pdf';
  const isImage = Boolean(file?.type?.startsWith('image/'));
  const imageUrl = useMemo(
    () => (file && isImage ? URL.createObjectURL(file) : ''),
    [file, isImage]
  );

  useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  return (
    <div className="relative flex h-[100px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-[#E5E6EB] lg:h-[128px] lg:w-[128px]">
      {isPdf && file ? (
        <div className="relative z-0 h-[95px] w-[68px] overflow-hidden rounded-[6px] bg-white shadow-[0px_4.8px_4.8px_0px_rgba(0,0,0,0.16)]">
          <PaywallPdfPage
            file={file}
            width={68}
            fallback={
              <PaywallThumbPlaceholder className="h-[95px] w-[68px] rounded-[6px] border-transparent" />
            }
          />
        </div>
      ) : isImage && imageUrl ? (
        <img
          src={imageUrl}
          alt={extension}
          className="relative z-0 h-[95px] w-[68px] rounded-[6px] bg-white object-cover shadow-[0px_4.8px_4.8px_0px_rgba(0,0,0,0.16)]"
        />
      ) : (
        <PaywallThumbPlaceholder className="h-[95px] w-[68px] rounded-[6px] border-transparent shadow-[0px_4.8px_4.8px_0px_rgba(0,0,0,0.16)]" />
      )}
      <span
        className={clsx(
          'pointer-events-none absolute left-[22px] top-[20px] z-10 rounded-[6px] border-2 border-solid border-white px-1 py-[3px] text-[10px] font-medium uppercase leading-none text-white',
          badge
        )}
      >
        {extension}
      </span>
      <div className="absolute left-[12px] top-[84px] hidden h-[32px] w-[32px] items-center justify-center rounded-[6px] bg-[#EEEEF2] lg:flex">
        <ZoomInIcon className="h-[18px] w-[18px]" />
      </div>
    </div>
  );
};

const Paywall1Summary = ({
  isOnlyPayment,
  hasTrial,
  planName,
  planSubtitle,
  dueTodayAmountText,
  recurringAmountText,
  firstChargeDate,
  recurringAdjective,
  documentName,
  documentExtension,
  documentFile,
}: {
  isOnlyPayment: boolean;
  hasTrial: boolean;
  planName: string;
  planSubtitle: string;
  dueTodayAmountText: string;
  recurringAmountText: string;
  firstChargeDate: string;
  recurringAdjective: string;
  documentName: string;
  documentExtension: string;
  documentFile?: File | null;
}) => {
  const { base: documentNameBase, suffix: documentNameSuffix } = splitFileName(
    documentName,
    documentExtension
  );

  return (
    <section>
      <h3 className="text-[20px] font-bold leading-6 text-[#292A33]">
        Order Summary
      </h3>
      <div className="mt-4 overflow-hidden rounded-[20px] bg-white">
        {!isOnlyPayment && (
          <div className="flex items-center gap-4 border-b border-[#EBEBEB] p-4">
            <Paywall1DocumentThumb
              file={documentFile}
              extension={documentExtension}
            />
            <div className="min-w-0">
              <p
                title={documentName}
                className="flex w-full max-w-[241px] text-[17px] font-semibold text-[#292A33] lg:max-w-[416px]"
              >
                <span className="-mr-1 min-w-0 truncate">
                  {documentNameBase}
                </span>
                <span className="shrink-0">{documentNameSuffix}</span>
              </p>
              <p className="mt-2 flex items-center gap-1.5 text-[17px] font-medium text-[#009E5E]">
                <ReadyCheckIcon className="h-6 w-6 shrink-0" />
                Your document is ready!
              </p>
            </div>
          </div>
        )}
        <div className="px-4 pb-4">
          <SummaryRow
            className="py-4"
            label={
              <span>
                <strong className="block text-[17px] font-semibold text-[#292A33]">
                  {planName}
                </strong>
                {hasTrial && (
                  <span className="text-[17px] text-[#787887]">
                    {planSubtitle}
                  </span>
                )}
              </span>
            }
            value={
              <span className="flex h-[46px] w-[46px] items-center justify-center rounded-full bg-[#4B7EFF]">
                <img
                  src={
                    hasTrial
                      ? '/images/paywall1/gift.png'
                      : '/images/paywall1/rocket.svg'
                  }
                  alt=""
                  className="h-6 w-6"
                />
              </span>
            }
          />
          <SummaryRow
            className="py-4"
            label={
              <span>
                <strong className="block text-[17px] font-semibold text-[#292A33]">
                  Due today
                </strong>
                {hasTrial && (
                  <span className="text-[17px] text-[#787887]">Trial</span>
                )}
              </span>
            }
            value={<PriceText value={dueTodayAmountText} />}
          />
          {hasTrial && (
            <SummaryRow
              className="py-4"
              label={`First ${recurringAdjective} charge ${firstChargeDate}`}
              value={recurringAmountText}
            />
          )}
          <SummaryRow
            className="py-4 text-[#787887]"
            label="Billing period"
            value={`Auto-renews at ${recurringAmountText} ${recurringAdjective} until cancelled`}
          />
          <SummaryRow
            className="pt-4 text-[#787887]"
            label="Cancel"
            value="Through your account"
            last
          />
        </div>
      </div>
    </section>
  );
};

const SummaryRow = ({
  label,
  value,
  className = '',
  last = false,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  className?: string;
  last?: boolean;
}) => (
  <div
    className={clsx(
      'flex min-h-[52px] items-center justify-between gap-20 text-[17px]',
      !last && 'border-b border-[#EBEBEB]',
      className
    )}
  >
    <span>{label}</span>
    <span className="max-w-[68%] text-right font-semibold leading-5 text-[#292A33]">
      {value}
    </span>
  </div>
);

const PriceText = ({ value }: { value: string }) => (
  <PaywallPriceText value={value} className="text-[#009E5E]" />
);

const Paywall1Layout = ({
  title,
  isOnlyPayment,
  hasTrial,
  planName,
  planSubtitle,
  dueTodayAmountText,
  recurringAmountText,
  firstChargeDate,
  recurringAdjective,
  documentName,
  documentExtension,
  documentFile,
  consentChecked,
  showConsentError,
  consentText,
  onConsentChange,
  checkoutMethod,
  checkoutContainerRef,
  paymentStatus,
  consentRef,
  shakeControls,
  onConsentRequired,
}: PaywallDocumentProps &
  PaywallCheckoutProps & {
    title: string;
    isOnlyPayment: boolean;
    hasTrial: boolean;
    planName: string;
    planSubtitle: string;
    dueTodayAmountText: string;
    recurringAmountText: string;
    firstChargeDate: string;
    recurringAdjective: string;
    consentChecked: boolean;
    showConsentError: boolean;
    consentText: React.ReactNode;
    onConsentChange: (checked: boolean) => void;
    consentRef: React.RefObject<HTMLDivElement>;
    shakeControls: AnimationControls;
    onConsentRequired: () => void;
  }) => {
  const summaryProps = {
    isOnlyPayment,
    hasTrial,
    planName,
    planSubtitle,
    dueTodayAmountText,
    recurringAmountText,
    firstChargeDate,
    recurringAdjective,
    documentName,
    documentExtension,
    documentFile,
  };

  return (
    <div className="bg-white font-barlow text-[#292A33]">
      <header className="flex h-[72px] items-center border-b border-[#EEEEF2] bg-white px-8 pt-2 lg:h-24">
        <div
          className={`mx-auto flex w-full ${PAYWALL_CONTENT_MAX_WIDTH_CLASS} items-center gap-3 lg:gap-4`}
        >
          <img
            src="/meta/icons/genius.svg"
            alt="PDF Genius"
            className="h-8 w-[25.6px]"
          />
          <h2 className="text-[22px] font-bold leading-7 lg:text-[28px] lg:leading-[34px]">
            {title}
          </h2>
        </div>
      </header>

      <div className="xl:grid xl:grid-cols-2">
        <div className="bg-[#F6F7F8] px-4 pt-4 lg:hidden">
          <Paywall1Summary {...summaryProps} />
        </div>

        <section className="px-4 pb-0 pt-6 lg:flex lg:justify-end lg:px-[32px] lg:py-6 xl:justify-start xl:px-0">
          <div className="w-full max-w-[560px] xl:ml-[64px]">
            <motion.div ref={consentRef} animate={shakeControls}>
              <div className="flex items-center gap-3">
                <h3 className="text-[20px] font-bold leading-6">
                  Payment Details
                </h3>
                {hasTrial && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-[#DAF6ED] px-1.5 py-1 text-[12px] font-medium text-[#009E5E]">
                    <TrialRocketIcon className="h-3 w-3 shrink-0" />
                    {planSubtitle.toUpperCase()}!
                  </span>
                )}
              </div>
              {showConsentError && (
                <p className="mt-3 text-[14px] text-[#F43F3F]">
                  You must check the box below
                </p>
              )}
              <label className="mt-4 flex cursor-pointer gap-3">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(event) => onConsentChange(event.target.checked)}
                  className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border border-[#787887] accent-[#4B7EFF]"
                />
                <span className="text-[14px] leading-5 text-[#292A33]">
                  {consentText}
                </span>
              </label>
            </motion.div>
            {checkoutMethod && (
              <div className="relative mt-8">
                <PaywallCheckoutFrame
                  checkoutContainerRef={checkoutContainerRef}
                  paymentStatus={paymentStatus}
                  minHeight={checkoutMethod === 'card' ? 450 : 560}
                  statusClassName="text-[#6D6D6D]"
                >
                  {!consentChecked && (
                    <button
                      type="button"
                      aria-label="You must check the box above to subscribe"
                      onClick={onConsentRequired}
                      className="absolute inset-0 z-10 cursor-pointer bg-transparent"
                    />
                  )}
                </PaywallCheckoutFrame>
              </div>
            )}
          </div>
        </section>

        <aside className="hidden bg-[#F6F7F8] px-8 py-8 xl:block">
          <div className="mx-auto w-full max-w-[592px]">
            <Paywall1Summary {...summaryProps} />
            <div className="mt-8">
              <Paywall1FeatureGrid />
            </div>
          </div>
        </aside>
        <div className="bg-[#F6F7F8] px-3 pb-6 pt-6 lg:hidden">
          <Paywall1FeatureGrid />
        </div>
      </div>
    </div>
  );
};

export default Paywall1Layout;
